/**
 * La scheda di una trattativa sul telefono (7/10/2026): risponde a "a che punto
 * è, e cosa faccio adesso?".
 *
 * Prima, a 390px, era un modulo: STEP / CHIUSURA PREVISTA / RINNOVO DI /
 * IMPORTO, un campo sotto l'altro con le etichette maiuscole, e la prossima
 * azione molto più giù. Qui:
 *   1. chi è, a che step è, chi la segue; sotto, i gesti per chiamare e
 *      scrivere (dalla trattativa si chiama, è il motivo per cui si apre);
 *   2. la prossima azione, subito: se manca lo si dice e la si programma;
 *   3. "A che punto è": step, chiusura prevista, valore, rinnovo, una riga
 *      ciascuna col valore a destra; toccandola si cambia (pannello dal basso);
 *   4. i requisiti del bando, se è un voucher; cosa ne è nato, se è vinta;
 *   5. lo storico, con la nota veloce.
 * "Vinta" in fondo; chiudere, passare a un collega, spostare di pipeline ed
 * eliminare nel menu ⋯ in alto, come nella pagina del computer. Il valore e il
 * contatto si modificano nei pannelli con gli stessi blocchi del computer.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { Icon } from '../../components/ui/Icon';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { MobileSheet } from '../../components/ui/MobileSheet';
import { PageActions } from '../../components/layout/PageActions';
import { fmtDate, fmtEur } from '../../lib/utils';
import { MActions, MCampo, MEmpty, MGroup, MHead, MMore, MPage, MRow, MScelte, leggibile } from '../../components/mobile/MobileKit';
import {
  canWriteDeals, counterparty, dealClientsQuery, dealContractsQuery, dealDetailQuery, dealOwnersQuery, dueInfo, fullName,
  REQUISITI, REQ_LABEL, type ReqStatusKey,
} from './dealsApi';
import { useDealActions } from './useDealActions';
import { ClosePopover } from './ClosePopover';
import { MovePipelinePopover } from './MovePipelinePopover';
import { NextActionPopover } from './NextActionPopover';
import { ContattoBlock, DopoIlSiBlock, ValoreBlock } from './DealDetailPage';

type Pannello = null | 'step' | 'chiusura' | 'rinnovo' | 'valore' | 'contatto' | 'titolo' | 'nota' | `req:${string}`;

/** Numero per WhatsApp: "333 123 4567" → "393331234567"; i fissi (con lo 0) no. */
function numeroWhatsApp(tel: string | null | undefined): string | null {
  if (!tel) return null;
  let n = tel.replace(/[^\d+]/g, '');
  if (n.startsWith('+')) n = n.slice(1);
  else if (n.startsWith('00')) n = n.slice(2);
  else if (n.startsWith('3') && n.length === 10) n = `39${n}`;
  if (n.startsWith('390')) return null;
  return n.length >= 10 ? n : null;
}

export function TrattativaDettaglioMobile({ id }: { id: string }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteDeals(user);
  const { update, win, close, movePipeline, nextAction, addEvent, remove } = useDealActions();
  const { data: deal, isLoading, isError } = useQuery({ ...dealDetailQuery(id), retry: false });
  const { data: owners } = useQuery(dealOwnersQuery);
  const { data: contracts } = useQuery(dealContractsQuery);
  const { data: clientsData } = useQuery(dealClientsQuery);
  const [aperto, setAperto] = useState<Pannello>(null);
  const [bozza, setBozza] = useState('');
  const [closing, setClosing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [storicoTutto, setStoricoTutto] = useState(false);

  if (isError) return <MPage><MEmpty>Trattativa non trovata: forse è stata eliminata, o il link è sbagliato.</MEmpty></MPage>;
  if (isLoading || !deal) return <MPage><MEmpty>Carico la trattativa…</MEmpty></MPage>;

  const azienda = counterparty(deal);
  const patch = (data: Record<string, unknown>) => update.mutate({ id: deal.id, data });
  const chiudi = () => setAperto(null);
  const apri = (p: Pannello, valore = '') => { if (!canWrite) return; setBozza(valore); setAperto(p); };
  const prevista = dueInfo(deal.expectedCloseDate, !deal.isOpen);
  const openStages = deal.stages.filter((s) => s.outcome !== 'PERSO');
  const lostStages = deal.stages.filter((s) => s.outcome === 'PERSO');
  const wonStage = deal.stages.find((s) => s.outcome === 'VINTO');
  const wa = numeroWhatsApp(deal.phone);
  const aperteDaFare = deal.tasks.filter((t) => t.status !== 'DONE');

  const sotto = [
    deal.stage.name,
    deal.owner ? `segue ${fullName(deal.owner)}` : null,
    deal.daysSinceLastEvent === 0 ? 'sentita oggi' : `ultimo movimento ${deal.daysSinceLastEvent} giorni fa`,
  ].filter(Boolean).join(' · ');

  return (
    <>
      {/* Il menu ⋯ nella barra in alto: le stesse voci del computer. */}
      <PageActions className="flex gap-2">
        <ActionsMenu
          variant="ghost"
          label="Azioni"
          disabled={remove.isPending}
          items={[
            { key: 'close', label: 'Chiudi la trattativa', icon: 'x', hidden: !canWrite || deal.isClosed || !lostStages.length, disabled: close.isPending, onClick: () => setClosing(true) },
            ...(owners ?? []).filter((u) => u.id !== deal.ownerId).map((u) => ({
              key: `own-${u.id}`, label: `Passa a ${fullName(u)}`, icon: 'user' as const, hidden: !canWrite, onClick: () => patch({ ownerId: u.id }),
            })),
            { key: 'move', label: "Sposta in un'altra pipeline", icon: 'swap', hidden: !canWrite, onClick: () => setMoving(true) },
            {
              key: 'delete', label: 'Elimina la trattativa', icon: 'trash', danger: true, hidden: !canWrite,
              onClick: async () => { const r = await remove.mutateAsync({ id: deal.id, title: azienda }); if (r) navigate('/trattative'); },
            },
          ]}
        />
      </PageActions>

      <MPage>
        <MHead title={leggibile(deal.title)} sub={sotto} onClick={canWrite ? () => apri('titolo', deal.title) : undefined} />

        {(deal.phone || deal.email) && (
          <MActions
            azioni={[
              { label: 'Chiama', icon: 'phone', href: deal.phone ? `tel:${deal.phone.replace(/\s+/g, '')}` : null },
              { label: 'Scrivi', icon: 'mail', href: deal.email ? `mailto:${deal.email}` : null },
              { label: 'WhatsApp', icon: 'message', href: wa ? `https://wa.me/${wa}` : null },
            ]}
          />
        )}

        {!deal.isClosed && (
          <MGroup title="Prossima azione" count={aperteDaFare.length || undefined}>
            {aperteDaFare.map((t) => {
              const d = dueInfo(t.dueDate, false);
              return (
                <MRow
                  key={t.id}
                  icon="tasks"
                  tone={t.isOverdue ? 'alert' : undefined}
                  title={t.title}
                  sub={[d.label !== '—' ? d.label : 'senza data', t.assignee ? t.assignee.firstName : null].filter(Boolean).join(' · ')}
                  onClick={() => navigate(`/tasks/${t.id}`)}
                />
              );
            })}
            {aperteDaFare.length === 0 && <MEmpty>Nessuna azione in programma: nessuno sa chi deve chiamare questo cliente.</MEmpty>}
            {canWrite && <MRow icon="calendarPlus" title="Programma un'azione" onClick={() => setPlanning(true)} />}
          </MGroup>
        )}

        <MGroup title="A che punto è">
          <MRow icon="kanban" title="Step" trail={deal.stage.name} trailTone="mute" onClick={canWrite && !deal.isClosed ? () => apri('step') : undefined} />
          {/* La data a destra, il ritardo sotto: "in ritardo di 7 g" a destra, a 360px, tagliava il titolo. */}
          <MRow
            icon="calendar"
            tone={prevista.tone === 'alert' ? 'alert' : undefined}
            title="Chiusura prevista"
            sub={deal.expectedCloseDate && prevista.tone !== 'mute' ? <span className={prevista.tone === 'alert' ? 'is-alert' : undefined}>{prevista.label}</span> : undefined}
            trail={deal.expectedCloseDate ? fmtDate(deal.expectedCloseDate) : 'nessuna'}
            trailTone="mute"
            onClick={canWrite ? () => apri('chiusura', deal.expectedCloseDate ?? '') : undefined}
          />
          <MRow icon="euro" title="Vale" trail={deal.value > 0 ? `€ ${fmtEur(deal.value)}` : 'senza valore'} trailTone={deal.value > 0 ? undefined : 'mute'} onClick={() => apri('valore')} chevron />
          <MRow
            icon="repeat"
            title="Rinnovo di"
            sub={deal.renewalOf ? `oggi vale € ${fmtEur(deal.renewalOf.annuo, true)} all'anno${deal.renewalOf.endDate ? `, scade il ${fmtDate(deal.renewalOf.endDate)}` : ''}` : undefined}
            trail={deal.renewalOf ? leggibile(deal.renewalOf.name) : 'non è un rinnovo'}
            trailTone="mute"
            onClick={canWrite ? () => apri('rinnovo') : undefined}
          />
        </MGroup>

        {deal.isVoucher && (
          <MGroup title="Requisiti del bando" note={!deal.requisitiOk ? `Da sistemare: ${deal.requisitiMancanti.join(', ')}.` : undefined}>
            {REQUISITI.map((r) => {
              const v = deal[r.key] as ReqStatusKey;
              return (
                <MRow
                  key={r.key}
                  icon={v === 'OK' ? 'check' : 'alert'}
                  tone={v === 'MANCA' ? 'alert' : undefined}
                  title={r.label}
                  sub={r.hint}
                  trail={REQ_LABEL[v]}
                  trailTone={v === 'MANCA' ? 'alert' : 'mute'}
                  onClick={canWrite ? () => apri(`req:${r.key}`) : undefined}
                />
              );
            })}
          </MGroup>
        )}

        <MGroup title="Contatto" action={canWrite ? { label: 'Modifica', onClick: () => apri('contatto') } : undefined}>
          {deal.client && <MRow icon="building" title={leggibile(deal.client.legalName ?? deal.client.shortName)} sub="scheda del cliente" onClick={() => navigate(`/clients/${deal.client!.id}`)} />}
          {deal.contactName && <MRow icon="user" title={deal.contactName} sub={[deal.phone, deal.email].filter(Boolean).join(' · ') || 'senza recapiti'} />}
          {!deal.client && !deal.contactName && <MEmpty>Nessun contatto scritto: aggiungilo con "Modifica".</MEmpty>}
        </MGroup>

        {/* Se è vinta: cosa ne è nato (progetto, rinnovo del canone). Stesso blocco del computer. */}
        {deal.isWon && <div className="mk-blocco"><DopoIlSiBlock deal={deal} canWrite={canWrite} /></div>}

        <MGroup title="Storico" count={deal.events.length || undefined} action={canWrite ? { label: 'Aggiungi una nota', onClick: () => apri('nota') } : undefined}>
          {deal.events.length === 0 && <MEmpty>Ancora niente.</MEmpty>}
          {deal.events.slice(0, storicoTutto ? undefined : 5).map((e) => (
            <MRow
              key={e.id}
              icon={e.kind === 'STAGE' ? 'kanban' : e.kind === 'CONTATTO' ? 'phone' : 'pencil'}
              title={`${fmtDate(e.createdAt)}${e.createdBy ? ` · ${e.createdBy.firstName}` : ''}`}
              sub={e.text}
              subWrap
            />
          ))}
          {deal.events.length > 5 && <MMore label={storicoTutto ? 'Mostra meno' : `Vedi tutti i ${deal.events.length}`} onClick={() => setStoricoTutto((v) => !v)} />}
        </MGroup>
      </MPage>

      {canWrite && wonStage && !deal.isWon && (
        <div className="m-actionbar m-only">
          <button
            className="btn btn-primary"
            disabled={win.isPending}
            onClick={() => win.mutate({ id: deal.id, title: azienda, stageId: wonStage.id, stageName: wonStage.name, prevStageId: deal.stageId })}
          >
            <Icon name="check" size={12} /> {wonStage.name}
          </button>
        </div>
      )}

      {/* ── Pannelli ── */}
      <MScelte
        open={aperto === 'step'}
        onClose={chiudi}
        titolo="Step della trattativa"
        opzioni={openStages.map((s) => ({ v: s.id, l: s.name, sub: s.outcome === 'VINTO' ? 'la trattativa è vinta' : `${s.probability}% di probabilità` }))}
        valore={deal.stageId}
        onScegli={(v) => { if (v !== deal.stageId) patch({ stageId: v }); chiudi(); }}
      />
      <MScelte
        open={aperto === 'rinnovo'}
        onClose={chiudi}
        titolo="Rinnovo di"
        nota="Il contratto in essere che questa trattativa rinnova, se non è denaro nuovo."
        opzioni={[{ v: '', l: 'Non è un rinnovo' }, ...(contracts ?? []).map((c) => ({ v: c.id, l: leggibile(c.name), sub: `€ ${fmtEur(c.annuo, true)} all'anno` }))]}
        valore={deal.renewalOf?.id ?? ''}
        onScegli={(v) => { patch({ renewalOfId: v || null }); chiudi(); }}
      />
      {REQUISITI.map((r) => (
        <MScelte
          key={r.key}
          open={aperto === `req:${r.key}`}
          onClose={chiudi}
          titolo={r.label}
          nota={r.hint}
          opzioni={(['OK', 'MANCA', 'DA_VERIFICARE'] as ReqStatusKey[]).map((s) => ({ v: s, l: REQ_LABEL[s] }))}
          valore={deal[r.key] as string}
          onScegli={(v) => { if (v !== deal[r.key]) patch({ [r.key]: v }); chiudi(); }}
        />
      ))}
      <MCampo
        open={aperto === 'chiusura'}
        onClose={chiudi}
        titolo="Chiusura prevista"
        nota="Quando pensiamo di chiuderla."
        campo={<input className="inp" type="date" value={bozza} onChange={(e) => setBozza(e.target.value)} />}
        onSalva={() => { patch({ expectedCloseDate: bozza || null }); chiudi(); }}
        onTogli={deal.expectedCloseDate ? () => { patch({ expectedCloseDate: null }); chiudi(); } : undefined}
        togli="Togli la data"
      />
      <MCampo
        open={aperto === 'nota'}
        onClose={chiudi}
        titolo="Aggiungi una nota allo storico"
        nota="Cos'è successo: una chiamata, una mail, un incontro."
        campo={<textarea className="inp" rows={4} value={bozza} onChange={(e) => setBozza(e.target.value)} />}
        onSalva={() => { const t = bozza.trim(); if (t) addEvent.mutate({ id: deal.id, kind: 'NOTA', text: t }); chiudi(); }}
      />
      <MCampo
        open={aperto === 'titolo'}
        onClose={chiudi}
        titolo="Nome della trattativa"
        campo={<input className="inp" value={bozza} onChange={(e) => setBozza(e.target.value)} />}
        onSalva={() => { const t = bozza.trim(); if (t && t !== deal.title) patch({ title: t }); chiudi(); }}
      />
      {/* Valore e contatto: gli stessi blocchi del computer, dentro un pannello. Salvano da soli. */}
      <MobileSheet open={aperto === 'valore'} onClose={chiudi} title="Quanto vale">
        <div className="mk-blocco"><ValoreBlock deal={deal} canWrite={canWrite} onPatch={patch} /></div>
      </MobileSheet>
      <MobileSheet open={aperto === 'contatto'} onClose={chiudi} title="Contatto">
        <div className="mk-blocco"><ContattoBlock deal={deal} canWrite={canWrite} onPatch={patch} clients={clientsData?.items ?? []} /></div>
      </MobileSheet>

      {closing && (
        <ClosePopover
          title={azienda}
          stages={lostStages}
          pending={close.isPending}
          onClose={() => setClosing(false)}
          onConfirm={(v) => { setClosing(false); close.mutate({ id: deal.id, title: azienda, prevStageId: deal.stageId, ...v }); }}
        />
      )}
      {moving && (
        <MovePipelinePopover
          title={azienda}
          currentPipelineId={deal.pipelineId}
          pending={movePipeline.isPending}
          onClose={() => setMoving(false)}
          onConfirm={(v) => { setMoving(false); movePipeline.mutate({ id: deal.id, title: azienda, ...v }); }}
        />
      )}
      {planning && (
        <NextActionPopover
          company={azienda}
          pending={nextAction.isPending}
          onClose={() => setPlanning(false)}
          onConfirm={(v) => { setPlanning(false); nextAction.mutate({ id: deal.id, ...v }); }}
        />
      )}
    </>
  );
}
